/**
 * Workspace seed manager - handles seed generation, wrapping, and storage
 */

import { generateWorkspaceSeed, wrapSeed, unwrapSeed, exportDevicePublicKey, importDevicePublicKey, getOrCreateDeviceKey, generateRecoveryPhrase, deriveRecoveryKeyPair, importRecoveryPublicKey, unwrapRecoverySeed } from '@yada/crypto';
import * as keyClient from '@yada/api-client';
import {
  relayWrappedSeed,
  notifySeedReady,
  onSeedRelay,
  startSignaling,
  waitForConnection,
  sendPairOffer,
  sendPairComplete,
  type PairOfferSeed,
  type PairOfferContext,
} from './signalingClient';

import { getAccount } from '@yada/storage';
import { getLocalWrappedSeeds, storeLocalWrappedSeed } from './localSeedStorage';
import { captureDeviceSyncScope, hydrateAccountWorkspaces, type WorkspaceHydrationReceipt } from './workspaceHydration';

/**
 * Thrown when a workspace seed is not found and allowCreate=false.
 * Used by CRDTStorage to distinguish "no seed yet" from decryption failures.
 */
export class WorkspaceSeedNotFoundError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'WorkspaceSeedNotFoundError';
  }
}

/**
 * Seed cache (in-memory) & in-flight request coalescer
 */
const seedCache = new Map<string, ArrayBuffer>();
const pendingSeedFetches = new Map<string, Promise<ArrayBuffer>>();
const completedPairOffers = new Set<string>();

/** Clear in-memory seed caches and completion records (used during local data purge). */
export function clearSeedCache(): void {
  seedCache.clear();
  pendingSeedFetches.clear();
  completedPairOffers.clear();
}

// Register signaling listener once at module load.
// When another device relays a seed directly to us, cache it immediately
// so that getWorkspaceSeed() finds it without hitting the auth server,
// then pull all workspace documents so they're live for SSE updates.
onSeedRelay((workspaceId, seed) => {
  const existing = seedCache.get(workspaceId);
  if (existing && !equalSeed(existing, seed)) {
    console.warn('[workspaceSeedManager] Rejected relay attempting to replace an existing workspace key');
    return;
  }
  seedCache.set(workspaceId, seed);
  console.log('[workspaceSeedManager] Cached relayed seed for workspace', workspaceId);

  // Dispatch DOM event so any locked CRDTStorage instances retry initialize().
  window.dispatchEvent(new CustomEvent('yada:workspace:seed-received', {
    detail: { workspaceId },
  }));

  // Pull all documents from the server now that we can decrypt them.
  // Without this, documents remain empty until the user manually navigates to them.
  import('./documentSync.js')
    .then(({ syncAllWorkspaceDocuments }) => syncAllWorkspaceDocuments(workspaceId))
    .catch(err => console.warn('[workspaceSeedManager] Failed to sync after P2P relay:', err));
});

/**
 * Create and store workspace seed for current profile
 */
export class WorkspaceKeyBackupError extends Error {
  constructor(public readonly workspaceId: string, public readonly stage: 'device' | 'recovery', cause: unknown) {
    super(`Workspace key preserved locally, but ${stage} backup is incomplete for ${workspaceId}. Retry backup before relying on another device. ${cause instanceof Error ? cause.message : 'Backup unavailable'}`);
    this.name = 'WorkspaceKeyBackupError';
  }
}

export async function createWorkspaceSeed(workspaceId: string, profileId: string): Promise<void> {
  const capture = await captureDeviceSyncScope();
  const deviceKey = await getOrCreateDeviceKey();
  const account = await getAccount();
  await capture.assertCurrent();
  // Discovery and migration must succeed before generating any new key.
  const local = await getLocalWrappedSeeds(workspaceId, { device_id: deviceKey.deviceId, wrap_type: 'device' });
  const remote = account?.isAccountless ? [] : await keyClient.getWrappedSeeds(workspaceId, { device_id: deviceKey.deviceId, wrap_type: 'device' });
  await capture.assertCurrent();
  if (!local.length && !remote.length && !account?.isAccountless) {
    const others = await keyClient.getWrappedSeeds(workspaceId);
    await capture.assertCurrent();
    if (others.length) throw new WorkspaceSeedNotFoundError('Original workspace key exists on another device. Restore it before creating a key.');
    const manifest = await keyClient.getDocumentManifest(workspaceId);
    await capture.assertCurrent();
    if (manifest.documents.some(document => !document.tombstone)) throw new WorkspaceSeedNotFoundError('Remote content exists. Restore its original workspace key before creating a key.');
  }
  const existing = local[0] || remote[0];
  const bytes = existing ? Uint8Array.from(atob(existing.wrapped_seed), byte => byte.charCodeAt(0)).buffer : null;
  const original = existing && bytes ? (existing.wrap_algorithm === 'raw' ? bytes : await unwrapSeed(bytes, deviceKey.privateKey)) : null;
  const seed = seedCache.get(workspaceId) || original || (await generateWorkspaceSeed(workspaceId)).seed;
  const prepared = await prepareOwnDeviceSeed(workspaceId, profileId, 'v1', seed, deviceKey);
  await capture.assertCurrent();
  await storeLocalWrappedSeed({ ...prepared.local, account_id: capture.scope.accountId });
  await capture.assertCurrent();
  seedCache.set(workspaceId, seed);
  if (!account?.isAccountless) {
    try { await capture.assertCurrent(); await keyClient.storeWrappedSeed(prepared.remote); await capture.assertCurrent(); }
    catch (error) { throw new WorkspaceKeyBackupError(workspaceId, 'device', error); }
  }
  try { await ensureRecoveryWrappedSeed(workspaceId, profileId, seed, capture); }
  catch (error) { throw new WorkspaceKeyBackupError(workspaceId, 'recovery', error); }
}

/**
 * Ensure a workspace seed is wrapped with the account's recovery public key,
 * if one has been registered.
 */
export async function ensureRecoveryWrappedSeed(
  workspaceId: string,
  profileId: string,
  seedBuffer: ArrayBuffer,
  originalCapture?: Awaited<ReturnType<typeof captureDeviceSyncScope>>
): Promise<void> {
  const capture = originalCapture || await captureDeviceSyncScope();
  await capture.assertCurrent();
  const account = await getAccount();
  await capture.assertCurrent();
  if (account?.isAccountless) return;
  const configuration = await keyClient.getPhraseRecoveryConfiguration();
  await capture.assertCurrent();
  if (configuration.accountId !== capture.scope.accountId) throw new Error('Recovery configuration belongs to another account');
  await ensurePasskeyWrappedSeed(workspaceId, profileId, seedBuffer, capture);
  if (!configuration.publicKey) {
    if (configuration.workspaces.some(workspace => workspace.recoveryCopyAvailable)) {
      throw new Error('Existing phrase backups need verified recovery-key enrollment before new workspace backups can be created. Enter your saved phrase in Security settings.');
    }
    return;
  }
  const copies = await keyClient.getWrappedSeeds(workspaceId, { wrap_type: 'recovery', account_owned: true });
  await capture.assertCurrent();
  const existing = copies.find(copy => copy.profile_id === profileId && copy.kid === 'recovery-v1' && !copy.device_id);
  // Without the phrase, an approved device cannot verify the recovery plaintext.
  // Preserve an existing immutable envelope; phrase recovery verifies it separately.
  if (existing) return;
  const recoveryPubKey = await importRecoveryPublicKey(configuration.publicKey);
  const wrapped = await wrapSeed(seedBuffer, recoveryPubKey);
  await capture.assertCurrent();
  await keyClient.storeWrappedSeed({ workspace_id: workspaceId, profile_id: profileId,
    kid: 'recovery-v1', wrapped_seed: btoa(String.fromCharCode(...new Uint8Array(wrapped))),
    wrap_algorithm: 'X25519-AES-GCM', wrap_type: 'recovery' });
  await capture.assertCurrent();
}

/** Future workspaces use each enrolled passkey's public recovery key. */
export async function ensurePasskeyWrappedSeed(workspaceId: string, profileId: string, seedBuffer: ArrayBuffer,
  originalCapture?: Awaited<ReturnType<typeof captureDeviceSyncScope>>): Promise<void> {
  const capture = originalCapture || await captureDeviceSyncScope();
  await capture.assertCurrent();
  if (capture.accountless) return;
  try {
    const keys = await keyClient.getPasskeyRecoveryKeys();
    await capture.assertCurrent();
    for (const key of keys) {
      const copies = await keyClient.getPasskeyRecoverySeeds(key.id, workspaceId);
      await capture.assertCurrent();
      const existing = copies.find(copy => copy.workspace_id === workspaceId && copy.profile_id === profileId && copy.kid === 'v1');
      // Only the passkey's PRF unlock can verify plaintext. Preserve prior immutable
      // copies here; possession of the public key cannot certify their contents.
      if (existing) continue;
      const publicKey = await importDevicePublicKey(key.public_key);
      const wrapped = await wrapSeed(seedBuffer, publicKey);
      await capture.assertCurrent();
      await keyClient.storePasskeySeed(key.id, { workspace_id: workspaceId, profile_id: profileId,
        kid: 'v1', wrapped_seed: btoa(String.fromCharCode(...new Uint8Array(wrapped))) });
      await capture.assertCurrent();
    }
  } catch (error) {
    throw new WorkspaceKeyBackupError(workspaceId, 'recovery', error);
  }
}

/**
 * Create recovery-wrapped seed for workspace and all other profile workspaces.
 *
 * Generates or uses a 24-word BIP39 recovery phrase, derives an asymmetric X25519
 * recovery keypair, wraps all workspace seeds with the recovery public key,
 * pins the canonical account public key, and stores the wrapped seeds on the server.
 *
 * @returns The recovery phrase (24 words) - user MUST save this!
 */
export class RecoverySetupError extends Error {
  constructor(public readonly recoveryPhrase: string, public readonly failedWorkspaceIds: string[]) {
    super(`Recovery setup incomplete for ${failedWorkspaceIds.length} workspace(s). Save this phrase and retry setup with the same phrase. Original keys and copies have been preserved.`);
    this.name = 'RecoverySetupError';
  }
}

export async function createRecoveryWrappedSeed(
  workspaceId: string,
  profileId: string,
  existingPhrase?: string
): Promise<string> {
  const capture = await captureDeviceSyncScope();
  const inventory = await keyClient.getAccountWorkspaceInventory();
  await capture.assertCurrent();
  if (inventory.accountId !== capture.scope.accountId) throw new Error('Recovery inventory belongs to another account');
  const workspaces = inventory.workspaces.filter(workspace => workspace.status === 'active' && workspace.encrypted);
  if (!workspaces.some(workspace => workspace.id === workspaceId)) throw new Error('Initialize this workspace before configuring recovery');
  const configuration = await keyClient.getPhraseRecoveryConfiguration();
  await capture.assertCurrent();
  if (configuration.accountId !== capture.scope.accountId) throw new Error('Recovery configuration belongs to another account');
  const previousPublicKey = configuration.publicKey;
  if (!existingPhrase) {
    if (previousPublicKey) throw new Error('Recovery is already configured. Enter your saved phrase to enable device recovery without replacing it.');
    for (const workspace of workspaces) {
      const copies = await keyClient.getWrappedSeeds(workspace.id, { wrap_type: 'recovery', account_owned: true });
      await capture.assertCurrent();
      if (copies.length) throw new Error('Existing recovery copies were found. Enter your saved recovery phrase; generating a replacement is disabled.');
    }
  }
  const recoveryPhrase = existingPhrase || await generateRecoveryPhrase();
  const { publicKey, publicKeyBase64 } = await deriveRecoveryKeyPair(recoveryPhrase);
  if (previousPublicKey && previousPublicKey !== publicKeyBase64) throw new Error('This phrase does not match the existing recovery key. Original recovery copies were preserved.');
  // Older accounts may have remote phrase copies but no locally cached public
  // key. Possession of an arbitrary valid mnemonic must not replace those copies.
  if (existingPhrase) {
    for (const workspace of workspaces) {
      const copies = await keyClient.getWrappedSeeds(workspace.id, { wrap_type: 'recovery', account_owned: true });
      await capture.assertCurrent();
      if (!copies.length) continue;
      const original = new Uint8Array(await getWorkspaceSeed(workspace.id, false));
      for (const copy of copies) {
        let recovered: Uint8Array;
        try {
          recovered = new Uint8Array(await unwrapRecoverySeed(Uint8Array.from(atob(copy.wrapped_seed), byte => byte.charCodeAt(0)).buffer,
            recoveryPhrase, copy.wrap_algorithm));
        } catch { throw new Error('This phrase does not unlock an existing recovery copy. Original phrase backups and keys were preserved.'); }
        await capture.assertCurrent();
        if (recovered.length !== original.length || recovered.some((byte, index) => byte !== original[index])) {
          throw new Error('The existing recovery copy contains a different workspace key. Original keys were preserved.');
        }
      }
    }
  }
  const prepared: Array<{workspaceId: string; profileId: string; encoded: string; algorithm: string}> = [];
  const failures: string[] = [];
  // Prepare complete existing-key coverage before pinning or writing any server state.
  for (const workspace of workspaces) {
    try {
      const seed = await getWorkspaceSeed(workspace.id, false);
      const copies = await keyClient.getWrappedSeeds(workspace.id, { wrap_type: 'recovery', account_owned: true });
      const existing = copies.find(copy => copy.profile_id === workspace.profileId && copy.kid === 'recovery-v1' && !copy.device_id);
      const wrapped = existing ? null : await wrapSeed(seed, publicKey);
      prepared.push({ workspaceId: workspace.id, profileId: workspace.profileId,
        encoded: existing?.wrapped_seed || btoa(String.fromCharCode(...new Uint8Array(wrapped!))), algorithm: existing?.wrap_algorithm || 'X25519-AES-GCM' });
    } catch { failures.push(workspace.id); }
    await capture.assertCurrent();
  }
  if (failures.length) throw new RecoverySetupError(recoveryPhrase, failures);
  try {
    await capture.assertCurrent();
    await keyClient.configurePhraseRecovery(publicKeyBase64);
    await capture.assertCurrent();
    // This public key is safe to persist, and retries must retain the original phrase.
    localStorage.setItem('yada:recovery:publicKey', publicKeyBase64);
  } catch { throw new RecoverySetupError(recoveryPhrase, workspaces.map(workspace => workspace.id)); }
  for (const copy of prepared) {
    try {
      await capture.assertCurrent();
      await keyClient.storeWrappedSeed({ workspace_id: copy.workspaceId, profile_id: copy.profileId,
        kid: 'recovery-v1', wrapped_seed: copy.encoded, wrap_algorithm: copy.algorithm, wrap_type: 'recovery' });
    } catch { failures.push(copy.workspaceId); }
  }
  await capture.assertCurrent();
  if (failures.length) throw new RecoverySetupError(recoveryPhrase, failures);
  await capture.assertCurrent();
  return recoveryPhrase;
}

/**
 * Recover workspace seed using recovery phrase.
 *
 * Supports both asymmetric X25519 recovery keys and legacy symmetric AES-GCM recovery keys.
 * Fetches recovery-wrapped seed from server, unwraps with phrase,
 * and re-wraps with current device key.
 *
 * @param workspaceId - Workspace to recover
 * @param recoveryPhrase - User's 24-word recovery phrase
 * @returns The unwrapped seed (also caches it and wraps for current device)
 */
export async function recoverWorkspaceSeedFromPhrase(
  workspaceId: string,
  recoveryPhrase: string
): Promise<ArrayBuffer> {
  console.log('[recoverWorkspaceSeedFromPhrase] Recovering seed for workspace:', workspaceId);
  const capture = await captureDeviceSyncScope();

  // Get profile ID
  const profileId = typeof localStorage !== 'undefined' ? localStorage.getItem('yada:account:activeProfileId') : null;
  if (!profileId) {
    throw new Error('No active profile - cannot recover seed');
  }

  // Derive recovery keypair to cache public key for future workspaces
  try {
    const { publicKeyBase64 } = await deriveRecoveryKeyPair(recoveryPhrase);
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('yada:recovery:publicKey', publicKeyBase64);
    }
  } catch (err) {
    console.warn('[recoverWorkspaceSeedFromPhrase] Could not cache recovery public key:', err);
  }

  // Fetch recovery-wrapped seeds
  const recoverySeeds = await keyClient.getWrappedSeeds(workspaceId, { wrap_type: 'recovery' });

  if (recoverySeeds.length === 0) {
    throw new Error('No recovery-wrapped seed found for this workspace');
  }

  // Use the first recovery seed
  const wrappedSeed = recoverySeeds[0];

  // Convert from base64
  const wrappedSeedBuffer = Uint8Array.from(
    atob(wrappedSeed.wrapped_seed),
    (c) => c.charCodeAt(0)
  ).buffer;

  // Unwrap seed with unwrapRecoverySeed (supports asymmetric X25519 & legacy symmetric AES-GCM)
  const seed = await unwrapRecoverySeed(wrappedSeedBuffer, recoveryPhrase, wrappedSeed.wrap_algorithm);

  console.log('[recoverWorkspaceSeedFromPhrase] Seed unwrapped successfully');

  // Recovery must survive a reload even when the key service is unavailable.
  // Persist only the device-wrapped seed; never the phrase or plaintext seed.
  const deviceKey = await getOrCreateDeviceKey();
  const prepared = await prepareOwnDeviceSeed(workspaceId, profileId, wrappedSeed.kid, seed, deviceKey);
  await capture.assertCurrent();
  await storeLocalWrappedSeed({ ...prepared.local, account_id: capture.scope.accountId });
  await capture.assertCurrent();
  const deviceSeed = prepared.remote;
  seedCache.set(workspaceId, seed);

  try {
    await capture.assertCurrent();
    await keyClient.storeWrappedSeed(deviceSeed);
    await capture.assertCurrent();
    console.log('[recoverWorkspaceSeedFromPhrase] Seed re-wrapped with device key');
  } catch (err) {
    console.warn('[recoverWorkspaceSeedFromPhrase] Server upload failed; device-wrapped seed is persisted locally:', err);
  }

  return seed;
}

/**
 * Get workspace seed (from cache or fetch and unwrap).
 *
 * @param workspaceId - Workspace to get seed for
 * @param allowCreate - If true (default), creates a new seed when none is found.
 *   Pass false for shared workspaces to prevent silently forking encryption —
 *   throws WorkspaceSeedNotFoundError instead so storage can enter locked state.
 */
export async function getWorkspaceSeed(workspaceId: string, allowCreate?: boolean): Promise<ArrayBuffer>;
export async function getWorkspaceSeed(workspaceId: string, allowCreate = true): Promise<ArrayBuffer> {
  // Check cache first
  const cached = seedCache.get(workspaceId);
  if (cached) {
    return cached;
  }

  // Coalesce concurrent in-flight fetches for the same workspace ID
  const fetchKey = `${workspaceId}:${allowCreate}`;
  const existingFetch = pendingSeedFetches.get(fetchKey);
  if (existingFetch) {
    return existingFetch;
  }

  const fetchPromise = (async () => {
    // Fetch wrapped seeds for the current device only.
    // Fall back to legacy seeds (device_id='') for databases migrated before per-device isolation.
    const deviceKey = await getOrCreateDeviceKey();
    const account = await getAccount();
    let wrappedSeeds: any[] = [];
    let seedFetchError: unknown;

    if (account?.isAccountless) {
      wrappedSeeds = await getLocalWrappedSeeds(workspaceId, { device_id: deviceKey.deviceId });
    } else {
      try {
        wrappedSeeds = await keyClient.getWrappedSeeds(workspaceId, { device_id: deviceKey.deviceId });
        if (wrappedSeeds.length === 0) {
          wrappedSeeds = await keyClient.getWrappedSeeds(workspaceId, { device_id: '', wrap_type: 'device' });
        }
      } catch (err) {
        seedFetchError = err;
        console.warn('[getWorkspaceSeed] Failed to fetch seeds from server:', err);
      }
      // Fallback: Check local seed storage if server returned no seeds (e.g. server wipe/recovery)
      if (wrappedSeeds.length === 0) {
        const localSeeds = await getLocalWrappedSeeds(workspaceId, { device_id: deviceKey.deviceId });
        if (localSeeds.length > 0) {
          console.log('[getWorkspaceSeed] Found local wrapped seed for workspace:', workspaceId);
          wrappedSeeds = localSeeds;
        }
      }
      // An unavailable key service is not evidence that a workspace is new.
      // Existing local keys remain usable offline; never generate a competing
      // encryption seed just because discovery failed.
      if (wrappedSeeds.length === 0 && seedFetchError !== undefined) throw seedFetchError;
    }

    if (wrappedSeeds.length === 0) {
      // Before creating a new seed, check whether ANY device already has one for this workspace.
      if (!account?.isAccountless) {
        try {
          const anyDeviceSeeds = await keyClient.getWrappedSeeds(workspaceId);
          if (anyDeviceSeeds.length > 0) {
            console.warn(
              `[getWorkspaceSeed] Seed exists on server for other device(s) but not this device (${deviceKey.deviceId}). ` +
              'Refusing to create a competing seed — waiting for P2P sync or device approval.'
            );
            throw new WorkspaceSeedNotFoundError(
              `Workspace ${workspaceId} is already encrypted by another device. Approve this device or sync keys to access.`
            );
          }
        } catch (err) {
          if (err instanceof WorkspaceSeedNotFoundError) throw err;
          console.warn('[getWorkspaceSeed] Failed to check existing device seeds on server:', err);
          throw err;
        }
      }

      if (!allowCreate) {
        console.warn(
          `[getWorkspaceSeed] No seed found for workspace ${workspaceId} and allowCreate=false. ` +
          'Refusing to silently create a new seed — entering locked storage state.'
        );
        throw new WorkspaceSeedNotFoundError(
          `Workspace seed not found for ${workspaceId}. This shared workspace may not have been initialised yet.`
        );
      }

      // Missing key rows alone do not establish that remote content is empty.
      // A lost key backup must never cause an original encrypted document to
      // acquire a competing seed. A failed manifest check also fails closed.
      if (!account?.isAccountless) {
        const manifest = await keyClient.getDocumentManifest(workspaceId);
        if (manifest.documents.some(document => !document.tombstone)) {
          throw new WorkspaceSeedNotFoundError(
            `Workspace ${workspaceId} contains remote data but its original encryption key is missing. Restore the original key before syncing.`
          );
        }
      }

      // No existing key or live remote content — safe to initialize the workspace.
      console.log('[getWorkspaceSeed] No seed found, creating new seed for workspace:', workspaceId);

      let profileId: string | null = null;
      for (let i = 0; i < 10; i++) {
        profileId = localStorage.getItem('yada:account:activeProfileId');
        if (profileId) break;
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      if (!profileId) {
        throw new Error('No active profile found - cannot create workspace seed. Please ensure you are logged in.');
      }

      // Upload device public key first if needed
      try {
        await uploadDevicePublicKey();
      } catch (err) {
        console.warn('[getWorkspaceSeed] Failed to upload device key (may already exist):', err);
      }

      // Create and store seed
      await createWorkspaceSeed(workspaceId, profileId);

      // Return the newly created seed (now in cache)
      const newSeed = seedCache.get(workspaceId);
      if (!newSeed) {
        throw new Error('Failed to create workspace seed');
      }
      return newSeed;
    }

    const wrapped = wrappedSeeds[0];
    const wrappedSeedBuffer = Uint8Array.from(
      atob(wrapped.wrapped_seed),
      (c) => c.charCodeAt(0)
    ).buffer;

    try {
      let seed: ArrayBuffer;
      if (wrapped.wrap_algorithm === 'raw') {
        seed = wrappedSeedBuffer;
      } else {
        seed = await unwrapSeed(
          wrappedSeedBuffer,
          deviceKey.privateKey
        );
      }

      seedCache.set(workspaceId, seed);
      if (!account?.isAccountless) void ensurePasskeyWrappedSeed(workspaceId, wrapped.profile_id, seed).catch(error => {
        console.warn('[getWorkspaceSeed] Original key remains usable; passkey backup is incomplete:', error);
      });

      if (!account?.isAccountless && (!wrapped.device_id || wrapped.device_id === '')) {
        const activeProfileId = typeof localStorage !== 'undefined'
          ? localStorage.getItem('yada:account:activeProfileId')
          : null;
        const profileId = activeProfileId || account?.id;
        if (profileId) {
          await prepareOwnDeviceSeed(workspaceId, profileId, 'v1', seed, deviceKey).then(async prepared => {
            await storeLocalWrappedSeed(prepared.local);
            await keyClient.storeWrappedSeed(prepared.remote);
            console.log('[getWorkspaceSeed] Synced/migrated wrapped seed to auth server for workspace:', workspaceId);
          }).catch((err: unknown) => {
            console.warn('[getWorkspaceSeed] Failed to persist wrapped seed to server:', err);
          });
        }
      }

      return seed;
    } catch (err) {
      console.error('[getWorkspaceSeed] Failed to decrypt workspace seed - device keys may have been regenerated');
      console.error('[getWorkspaceSeed] Error:', err);
      throw new Error(
        'Cannot decrypt workspace seed: device encryption keys do not match. ' +
        'This can happen if browser data was partially cleared. ' +
        'Encrypted content will be inaccessible until keys are re-established.'
      );
    }
  })();

  pendingSeedFetches.set(fetchKey, fetchPromise);
  try {
    return await fetchPromise;
  } finally {
    pendingSeedFetches.delete(fetchKey);
  }
}

/**
 * Status returned by uploadDevicePublicKey so callers can react to key regeneration.
 * - 'ok'       — key uploaded or already matched
 * - 'mismatch' — local key differs from server key AND seeds exist → upload blocked to protect data
 * - 'skipped'  — server unreachable, upload deferred
 */
export type DeviceKeyUploadStatus = 'ok' | 'mismatch' | 'skipped';

/**
 * Upload device public key to auth server.
 *
 * Safety: If the key was regenerated (IndexedDB cleared), uploading would
 * overwrite the old public key on the server. Since existing workspace seeds
 * are wrapped with the OLD key, this would make them permanently undecryptable.
 * In that case, we skip the upload to preserve recovery options.
 *
 * Returns 'mismatch' when this safety check fires — callers should surface a
 * recovery prompt to the user so they can restore access via recovery phrase.
 */
export async function uploadDevicePublicKey(options?: { force?: boolean }): Promise<DeviceKeyUploadStatus> {
  const account = await getAccount();
  if (account?.isAccountless) {
    console.log('[uploadDevicePublicKey] Skipping upload for accountless account');
    return 'ok';
  }

  const deviceKey = await getOrCreateDeviceKey();
  const publicKeyBase64 = await exportDevicePublicKey(deviceKey.publicKey);

  // Always compare with server's stored key before uploading.
  // This prevents overwriting a key that seeds are wrapped with — if our local key was regenerated
  // (IndexedDB cleared but localStorage preserved the deviceId), uploading the new key would
  // permanently orphan all seeds wrapped with the original key.
  let existingServerKey: Awaited<ReturnType<typeof keyClient.getDeviceKey>> = null;
  try {
    existingServerKey = await keyClient.getDeviceKey(deviceKey.deviceId);
  } catch {
    // Cannot reach server — skip upload to be safe
    console.warn('[uploadDevicePublicKey] Cannot reach server to check existing key — skipping upload');
    return 'skipped';
  }

  if (existingServerKey) {
    if (existingServerKey.public_key === publicKeyBase64) {
      // Server already has the correct key — nothing to do
      return 'ok';
    }

    if (!options?.force) {
      // Check if this device is pending approval on the server.
      // If the device is currently pending, it does not hold valid keys for previous seeds,
      // and uploading the new public key is necessary so the approving device can encrypt seeds for it.
      let isPending = false;
      try {
        const { getDeviceStatus } = await import('@yada/api-client');
        const status = await getDeviceStatus(deviceKey.deviceId);
        isPending = status.status === 'pending';
      } catch {
        // Assume not pending if status check fails
      }

      if (!isPending) {
        // Keys differ and device is already approved — check if seeds exist wrapped with old key.
        const seedsForDevice = await keyClient.getWrappedSeeds(undefined, {
          device_id: deviceKey.deviceId,
          wrap_type: 'device',
        });
        if (seedsForDevice.length > 0) {
          console.warn('[uploadDevicePublicKey] Device key mismatch: local key differs from server key, and seeds exist wrapped with server key.');
          console.warn('[uploadDevicePublicKey] Skipping upload to preserve seed access. Use recovery phrase or an approved device to restore access.');
          return 'mismatch';
        }
      }
    }
  }

  await keyClient.storeDeviceKey({
    device_id: deviceKey.deviceId,
    public_key: publicKeyBase64,
    algorithm: deviceKey.algorithm,
  });

  console.log('[uploadDevicePublicKey] Device public key uploaded');
  return 'ok';
}

/**
 * Share workspace seed with new member
 *
 * When adding a new member to workspace:
 * 1. Get their device public key from auth server
 * 2. Wrap workspace seed with their key
 * 3. Store wrapped seed for them
 */
export async function shareWorkspaceSeed(
  workspaceId: string,
  profileId: string
): Promise<void> {
  // Get workspace seed (will fetch and unwrap if needed)
  const seed = await getWorkspaceSeed(workspaceId);

  // Get member's device keys
  const memberKeys = await keyClient.getMemberDeviceKeys(workspaceId);

  const targetKeys = memberKeys.filter((k: { profile_id: string }) => k.profile_id === profileId);
  if (targetKeys.length === 0) {
    throw new Error('Member device key not found');
  }

  // Import member's public key and wrap for all their devices
  const { importDevicePublicKey } = await import('@yada/crypto');
  for (const memberKey of targetKeys) {
    const memberPublicKey = await importDevicePublicKey(memberKey.device_key.public_key);
    const prepared = await prepareTargetDeviceSeed(workspaceId, profileId, 'v1', memberKey.device_key.device_id, memberPublicKey, seed);
    await keyClient.storeWrappedSeed(prepared);
  }

  console.log(`[shareWorkspaceSeedWithMember] Shared seed for workspace ${workspaceId} with profile ${profileId} across ${targetKeys.length} devices`);
}

/**
 * Share all workspace seeds with a newly approved device
 *
 * This is called when an existing approved device approves a new device.
 * For each workspace the current user has access to, this unwraps the seed
 * using the current device's private key, re-wraps it with the new device's
 * public key, and stores it on the server.
 *
 * @param deviceId - ID of the newly approved device
 */
export async function shareWorkspaceSeedsWithDevice(deviceId: string): Promise<void> {
  console.log('[shareWorkspaceSeedsWithDevice] Starting seed sharing for device:', deviceId);

  const capture = await captureDeviceSyncScope();
  // Get the target device's public key
  const deviceKey = await keyClient.getDeviceKey(deviceId);
  if (!deviceKey) {
    throw new Error('Device public key not found');
  }

  // Import device's public key
  const { importDevicePublicKey } = await import('@yada/crypto');
  const devicePublicKey = await importDevicePublicKey(deviceKey.public_key);

  const inventory = await keyClient.getAccountWorkspaceInventory();
  await capture.assertCurrent();
  if (inventory.accountId !== capture.scope.accountId) throw new Error('Workspace inventory belongs to another account');
  const workspaces = inventory.workspaces.filter(workspace => workspace.status === 'active' && workspace.encrypted);

  console.log(`[shareWorkspaceSeedsWithDevice] Found ${workspaces.length} workspaces to share`);

  // For each workspace, unwrap seed and re-wrap with new device's key
  const relayPayloads: Array<{ workspace_id: string; wrapped_seed: string; wrap_algorithm: string }> = [];
  const failedWorkspaces: Array<{ id: string; reason: string }> = [];

  for (const workspace of workspaces) {
    try {
      // Get workspace seed (this unwraps it with current device's private key)
      const seed = await getWorkspaceSeed(workspace.id, false);

      const prepared = await prepareTargetDeviceSeed(workspace.id, workspace.profileId, workspace.kid || 'v1', deviceId, devicePublicKey, seed);
      await capture.assertCurrent();
      await keyClient.storeWrappedSeed(prepared);
      const wrappedSeedBase64 = prepared.wrapped_seed, wrapAlgorithm = prepared.wrap_algorithm;

      relayPayloads.push({ workspace_id: workspace.id, wrapped_seed: wrappedSeedBase64, wrap_algorithm: wrapAlgorithm });

      console.log(`[shareWorkspaceSeedsWithDevice] Stored seed for workspace ${workspace.id}`);
    } catch (err) {
      console.error(`[shareWorkspaceSeedsWithDevice] Failed for workspace ${workspace.id}:`, err);
      failedWorkspaces.push({ id: workspace.id, reason: err instanceof Error ? err.message : String(err) });
      // Continue with other workspaces
    }
  }

  // P2P fast path: relay all wrapped seeds directly to the target device if it's online.
  // The target device receives and decrypts immediately, skipping an auth-server round-trip.
  // Seeds are already RSA-OAEP encrypted — the relay server cannot read them.
  let relayed = false;
  await capture.assertCurrent();
  for (const p of relayPayloads) {
    const sent = relayWrappedSeed(deviceId, {
      workspace_id: p.workspace_id,
      wrapped_seed: p.wrapped_seed,
      kid: 'v1',
      wrap_algorithm: p.wrap_algorithm,
    });
    if (sent) relayed = true;
  }

  if (!relayed) {
    // Device is offline — send a seed:ready ping so it fetches from auth server
    // once it comes back online and reconnects to the signaling channel.
    notifySeedReady(deviceId);
  }

  if (failedWorkspaces.length > 0) {
    throw new Error(
      `Device linking incomplete: keys could not be shared for ${failedWorkspaces.length} of ${workspaces.length} workspaces. ` +
      `Failed: ${failedWorkspaces.map(({ id, reason }) => `${id}: ${reason}`).join('; ')}. ` +
      'Keys already delivered have been preserved. Retry from a device with access to all workspaces.'
    );
  }

  console.log(`[shareWorkspaceSeedsWithDevice] All seeds shared (P2P relay: ${relayed ? 'yes' : 'no — device offline, server fallback active'})`);
}

/**
 * Share a workspace seed with all devices belonging to a specific profile.
 *
 * Used during DM workspace creation so the invitee can decrypt the workspace
 * before they formally accept. Fetches the target's device public keys directly
 * (no workspace membership required) and stores a wrapped copy for each device.
 *
 * Throws if the target profile has no registered device keys.
 */
export async function shareSeedWithProfileDevices(
  workspaceId: string,
  targetProfileId: string
): Promise<void> {
  console.log('[shareSeedWithProfileDevices] Sharing seed for workspace', workspaceId, 'with profile', targetProfileId);

  // Get workspace seed (already wrapped for our device — unwraps it)
  const seed = await getWorkspaceSeed(workspaceId);

  // Fetch target profile's device public keys directly (bypasses workspace membership check)
  const targetDeviceKeys = await keyClient.getProfileDeviceKeys(targetProfileId);

  if (targetDeviceKeys.length === 0) {
    throw new Error(
      'Target profile has no registered device keys. ' +
      'They must log in at least once before you can message them.'
    );
  }

  // Wrap and store seed for each of the target's devices
  for (const deviceKey of targetDeviceKeys) {
    try {
      const publicKey = await importDevicePublicKey(deviceKey.public_key);
      const prepared = await prepareTargetDeviceSeed(workspaceId, targetProfileId, 'v1', deviceKey.device_id, publicKey, seed);
      await keyClient.storeWrappedSeed(prepared);

      console.log('[shareSeedWithProfileDevices] Shared seed for device', deviceKey.device_id);
    } catch (err) {
      console.error('[shareSeedWithProfileDevices] Failed for device', deviceKey.device_id, err);
      // Continue — at least some devices should get the seed
    }
  }

  console.log('[shareSeedWithProfileDevices] Done');
}

/**
 * Fetch all workspace seeds for the current device from the auth server,
 * decrypt them, and populate the in-memory cache.
 *
 * Also dispatches `yada:workspace:seed-received` for each workspace that was
 * newly unlocked, which causes locked CRDTStorage instances to retry
 * initialisation and become usable without a page reload.
 *
 * After unlocking seeds, triggers sync for all active documents so Device B
 * can receive data from Device A immediately after approval — no manual refresh needed.
 *
 * Call this on boot (after device approval) and after returning from the
 * pending state so previously locked content unlocks immediately.
 */
export function prefetchWorkspaceSeeds(): Promise<WorkspaceHydrationReceipt> {
  return hydrateAccountWorkspaces();
}

/**
 * Device A: wrap all workspace seeds with Device B's ephemeral public key
 * and send them via the signaling relay.
 *
 * This is the zero-knowledge path: seeds never touch the auth server.
 * The signaling server only routes an opaque encrypted blob.
 *
 * @param sessionId - Device B's pairing session ID (used as relay address)
 * @param ephemeralPublicKey - Device B's ephemeral RSA public key
 */
export async function pairWithDevice(
  sessionId: string,
  ephemeralPublicKey: CryptoKey,
  targetDeviceId?: string
): Promise<void> {
  console.log('[pairWithDevice] Starting key handoff to session', sessionId);
  if (!targetDeviceId) throw new Error('The new device identity is missing. Generate a new pairing session.');
  const capture = await captureDeviceSyncScope();

  await startSignaling();
  await waitForConnection(10_000);

  const inventory = await keyClient.getAccountWorkspaceInventory();
  await capture.assertCurrent();
  if (inventory.accountId !== capture.scope.accountId) throw new Error('Workspace inventory belongs to another account');
  const workspaces = inventory.workspaces.filter(workspace => workspace.status === 'active' && workspace.encrypted);
  const workspaceIds = new Set(workspaces.map(workspace => workspace.id));

  const seeds: PairOfferSeed[] = [];
  const failedWorkspaces: string[] = [];

  for (const wsId of workspaceIds) {
    try {
      const seed = await getWorkspaceSeed(wsId, false);
      const wrappedSeed = await wrapSeed(seed, ephemeralPublicKey);
      const wrappedSeedBase64 = btoa(String.fromCharCode(...new Uint8Array(wrappedSeed)));
      const wrapAlgorithm = ephemeralPublicKey.algorithm.name === 'X25519' ? 'X25519-AES-GCM' : 'RSA-OAEP-256';
      seeds.push({ workspace_id: wsId, wrapped_seed: wrappedSeedBase64, wrap_algorithm: wrapAlgorithm });
      console.log('[pairWithDevice] Wrapped seed for workspace', wsId);
    } catch (err) {
      console.warn('[pairWithDevice] Failed to wrap seed for workspace', wsId, err);
      failedWorkspaces.push(wsId);
    }
  }

  if (failedWorkspaces.length > 0) {
    throw new Error(
      `Device linking incomplete: keys could not be prepared for ${failedWorkspaces.length} of ${workspaceIds.size} workspaces. ` +
      'Pairing stopped. Retry from a device with access to all workspaces.'
    );
  }

  let wrappedAuthBase64: string | undefined;
  const pairing = { sessionId, offerId: crypto.randomUUID().replace(/-/g, ''),
    receiptToken: crypto.randomUUID().replace(/-/g, '') + crypto.randomUUID().replace(/-/g, ''),
    senderDeviceId: capture.scope.deviceId };
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('yada:access_token') : null;
  const accountRaw = typeof localStorage !== 'undefined' ? localStorage.getItem('yada:account:account') : null;
  const profileId = typeof localStorage !== 'undefined' ? localStorage.getItem('yada:account:activeProfileId') : null;
  const defaultStorageUrl = typeof localStorage !== 'undefined' ? localStorage.getItem('yada:defaultStorageUrl') : null;
  const defaultRelayUrl = typeof localStorage !== 'undefined' ? localStorage.getItem('yada:defaultRelayUrl') : null;
  const currentWsId = typeof localStorage !== 'undefined' ? (localStorage.getItem('yada:currentWorkspaceId') || localStorage.getItem('yada:activeWorkspaceId')) : null;

  if (token && accountRaw) {
    try {
      const account = JSON.parse(accountRaw);
      if (account.id !== capture.scope.accountId) throw new Error('Account changed during device linking');
      let deviceAccessToken: string | undefined;
      let deviceRefreshToken: string | undefined;

      const deviceToMint = targetDeviceId;
      try {
        await capture.assertCurrent();
        const res = await keyClient.fetchWithAuth(`${keyClient.AUTH_API_URL}/api/devices/${encodeURIComponent(deviceToMint)}/token`, {
          method: 'POST',
        });
        if (res.ok) {
          const data = await res.json();
          deviceAccessToken = data.access_token;
          deviceRefreshToken = data.refresh_token;
        }
      } catch (tokenErr) {
        console.warn('[pairWithDevice] Failed to issue dedicated device credentials:', tokenErr);
      }

      if (!deviceAccessToken || !deviceRefreshToken) {
        throw new Error('Could not issue dedicated credentials for the new device');
      }

      const authPayload = JSON.stringify({
        pairing,
        expectedWorkspaceIds: [...workspaceIds],
        accessToken: deviceAccessToken,
        refreshToken: deviceRefreshToken,
        email: account.email || '',
        accountId: account.id || '',
        profileId: profileId || account.profile_id || '',
        deviceId: deviceToMint,
        storageUrl: defaultStorageUrl || '',
        relayUrl: defaultRelayUrl || '',
        activeWorkspaceId: currentWsId || '',
        emailVerified: account.emailVerified ?? true,
      });
      const authBuffer = new TextEncoder().encode(authPayload).buffer;
      const wrappedAuth = await wrapSeed(authBuffer, ephemeralPublicKey);
      wrappedAuthBase64 = btoa(String.fromCharCode(...new Uint8Array(wrappedAuth)));
    } catch (e) {
      console.warn('[pairWithDevice] Failed to wrap auth info:', e);
      throw new Error('Could not issue dedicated device credentials. Pairing stopped.');
    }
  }

  await capture.assertCurrent();
  const sent = sendPairOffer(sessionId, seeds, wrappedAuthBase64, {
    offerId: pairing.offerId, receiptToken: pairing.receiptToken, targetDeviceId,
  });
  if (!sent) {
    throw new Error(
      'Could not reach the other device. Make sure it is connected and the pairing screen is still open.'
    );
  }

  console.log('[pairWithDevice] Sent', seeds.length, 'seeds and auth bundle to session', sessionId);
}

/**
 * Device B: receive a pair:offer, decrypt seeds with the ephemeral private key,
 * re-wrap with the long-term device key, cache, and notify listeners.
 *
 * After this completes, call sendPairComplete() to confirm to Device A,
 * then uploadDevicePublicKey() to register on the auth server.
 *
 * @param seeds - Encrypted seed bundles from Device A
 * @param ephemeralPrivateKey - This session's ephemeral RSA private key
 * @param fromDeviceId - Device A's device_id, used to send pair:complete
 */
export async function acceptPairOffer(
  seeds: PairOfferSeed[],
  ephemeralPrivateKey: CryptoKey,
  fromDeviceId: string,
  wrappedAuth?: string,
  context?: PairOfferContext,
  onAuthenticated?: () => void,
  assertOfferCurrent?: () => void,
): Promise<WorkspaceHydrationReceipt> {
  assertOfferCurrent?.();
  const capture = await captureDeviceSyncScope();
  const assertCurrent = async () => {
    assertOfferCurrent?.();
    await capture.assertCurrent();
    assertOfferCurrent?.();
  };
  await assertCurrent();
  if (!wrappedAuth || !context || context.fromDeviceId !== fromDeviceId || context.accountId !== capture.scope.accountId) {
    throw new Error('Unverified pairing offer. Generate a new session on the receiving device.');
  }
  const offerIdentity = JSON.stringify([capture.scope.accountId, capture.scope.deviceId, context.sessionId, context.offerId]);
  if (completedPairOffers.has(offerIdentity)) throw new Error('This pairing offer has already completed. Generate a new session.');
  const authBuffer = Uint8Array.from(atob(wrappedAuth), c => c.charCodeAt(0)).buffer;
  const authData = JSON.parse(new TextDecoder().decode(await unwrapSeed(authBuffer, ephemeralPrivateKey))) as {
    accountId?: string; deviceId?: string; accessToken?: string; refreshToken?: string;
    expectedWorkspaceIds?: string[];
    pairing?: { sessionId?: string; offerId?: string; receiptToken?: string; senderDeviceId?: string };
  };
  const proof = authData.pairing;
  if (!proof || proof.sessionId !== context.sessionId || proof.offerId !== context.offerId ||
    proof.senderDeviceId !== fromDeviceId || !/^[a-f0-9]{64,128}$/.test(proof.receiptToken || '') ||
    authData.accountId !== capture.scope.accountId || authData.deviceId !== capture.scope.deviceId ||
    typeof authData.accessToken !== 'string' || !authData.accessToken ||
    typeof authData.refreshToken !== 'string' || !authData.refreshToken ||
    !Array.isArray(authData.expectedWorkspaceIds) || authData.expectedWorkspaceIds.some(id => typeof id !== 'string')) {
    throw new Error('Pairing identity or session does not match this device. No keys were installed.');
  }
  const expected = new Set(authData.expectedWorkspaceIds);
  if (expected.size !== authData.expectedWorkspaceIds.length || seeds.length !== expected.size ||
    new Set(seeds.map(seed => seed.workspace_id)).size !== seeds.length || seeds.some(seed => !expected.has(seed.workspace_id))) {
    throw new Error('The pairing offer does not include every expected workspace key. Retry linking.');
  }
  await assertCurrent();
  const sessionReceipt = await fetch(`${keyClient.AUTH_API_URL}/api/auth/devices/signaling-authorize`, {
    method: 'POST', headers: { Authorization: `Bearer ${authData.accessToken}` },
  });
  const authorized = await sessionReceipt.json();
  await assertCurrent();
  if (!sessionReceipt.ok || authorized.approved !== true || authorized.accountId !== capture.scope.accountId ||
    authorized.deviceId !== capture.scope.deviceId) throw new Error('Recipient credentials could not be verified. Original session preserved.');
  if ((await keyClient.getDeviceStatus(capture.scope.deviceId)).status !== 'approved') {
    throw new Error('Device approval is incomplete. Approve the verified device before receiving data.');
  }
  const inventory = await keyClient.getAccountWorkspaceInventory();
  await assertCurrent();
  if (inventory.accountId !== capture.scope.accountId) throw new Error('Workspace inventory belongs to another account');
  const workspaces = inventory.workspaces.filter(workspace => workspace.status === 'active' && workspace.encrypted);
  const byId = new Map(workspaces.map(workspace => [workspace.id, workspace]));
  if (byId.size !== expected.size || [...expected].some(id => !byId.has(id))) {
    throw new Error('Workspace access changed during linking. Generate a fresh pairing offer.');
  }
  // A receiving view may keep this exact verified offer in memory for retry.
  // This is permission to retry key/content checks, never a completion receipt.
  onAuthenticated?.();
  await assertCurrent();
  const deviceKey = await getOrCreateDeviceKey();
  await assertCurrent();
  const uploaded = await uploadDevicePublicKey();
  if (uploaded !== 'ok') throw new Error('Device encryption key registration is incomplete. Original keys have been preserved.');
  await assertCurrent();
  // The encrypted, identity-bound dedicated session covers every account profile.
  keyClient.setAccessToken(authData.accessToken, 900);
  const failedWorkspaces: string[] = [];
  for (const { workspace_id, wrapped_seed } of seeds) {
    try {
      await assertCurrent();
      const incoming = await unwrapSeed(Uint8Array.from(atob(wrapped_seed), c => c.charCodeAt(0)).buffer, ephemeralPrivateKey);
      if (incoming.byteLength !== 32) throw new Error('Invalid workspace seed length');
      const previous = seedCache.get(workspace_id);
      if (previous && !equalSeed(previous, incoming)) throw new Error('Cannot replace an existing workspace encryption key');
      const workspace = byId.get(workspace_id)!;
      const prepared = await prepareOwnDeviceSeed(workspace_id, workspace.profileId, workspace.kid || 'v1', incoming, deviceKey);
      await assertCurrent();
      await storeLocalWrappedSeed({ ...prepared.local, account_id: capture.scope.accountId });
      const persisted = (await getLocalWrappedSeeds(workspace_id, { device_id: deviceKey.deviceId, wrap_type: 'device' }))[0];
      if (!persisted || !equalSeed(await unwrapSeed(Uint8Array.from(atob(persisted.wrapped_seed), c => c.charCodeAt(0)).buffer,
        deviceKey.privateKey), incoming)) throw new Error('Local key persistence could not be verified');
      await assertCurrent();
      seedCache.set(workspace_id, incoming);
      window.dispatchEvent(new CustomEvent('yada:workspace:seed-received', { detail: { workspaceId: workspace_id } }));
      // Backups are part of the receipt. A failed upload retains the verified local key for retry.
      await keyClient.storeWrappedSeed(prepared.remote);
    } catch (error) {
      await assertCurrent();
      failedWorkspaces.push(workspace_id);
      console.warn('[acceptPairOffer] Key delivery incomplete for workspace', workspace_id, error);
    }
  }
  if (failedWorkspaces.length) throw new Error(
    `Device linking incomplete: keys could not be received or backed up for ${failedWorkspaces.length} of ${seeds.length} workspaces. ` +
    'Keys already received have been preserved. Retry linking from the existing device.');
  const receipt = await prefetchWorkspaceSeeds();
  await assertCurrent();
  if (!receipt.complete) {
    throw new Error(
      `Device linking incomplete: ${receipt.syncedWorkspaces} of ${receipt.totalWorkspaces} workspaces synced. ` +
      'Keys already received have been preserved. Retry sync.'
    );
  }
  if (!sendPairComplete(fromDeviceId, { ...context, receiptToken: proof.receiptToken! })) {
    throw new Error('Data synced, but the other device could not receive confirmation. Keep this screen open and retry linking.');
  }
  completedPairOffers.add(offerIdentity);
  if (completedPairOffers.size > 1024) completedPairOffers.delete(completedPairOffers.values().next().value!);
  return receipt;
}

function equalSeed(left: ArrayBuffer, right: ArrayBuffer): boolean {
  const a = new Uint8Array(left), b = new Uint8Array(right);
  return a.length === b.length && a.every((byte, index) => byte === b[index]);
}

/** Keep each vault/server identity's original ciphertext even when wrapping is randomized. */
async function prepareOwnDeviceSeed(workspaceId: string, profileId: string, kid: string, seed: ArrayBuffer,
  deviceKey: Awaited<ReturnType<typeof getOrCreateDeviceKey>>) {
  const capture = await captureDeviceSyncScope();
  const account = await getAccount();
  await capture.assertCurrent();
  const local = await getLocalWrappedSeeds(workspaceId, { device_id: deviceKey.deviceId, wrap_type: 'device' });
  const remote = account?.isAccountless ? [] : await keyClient.getWrappedSeeds(workspaceId, { device_id: deviceKey.deviceId, wrap_type: 'device' });
  await capture.assertCurrent();
  for (const copy of [...local, ...remote]) {
    if ('account_id' in copy && copy.account_id && copy.account_id !== capture.scope.accountId) throw new Error('Existing workspace key belongs to another account');
    const bytes = Uint8Array.from(atob(copy.wrapped_seed), byte => byte.charCodeAt(0)).buffer;
    const original = copy.wrap_algorithm === 'raw' ? bytes : await unwrapSeed(bytes, deviceKey.privateKey);
    if (!equalSeed(original, seed)) throw new Error('Cannot replace an existing workspace encryption key');
  }
  const matches = (copy: { profile_id: string; kid: string }) => copy.profile_id === profileId && copy.kid === kid;
  const localCopy = local.find(matches), remoteCopy = remote.find(matches);
  const reused = localCopy || remoteCopy;
  const encoded = reused?.wrapped_seed || btoa(String.fromCharCode(...new Uint8Array(await wrapSeed(seed, deviceKey.publicKey))));
  const algorithm = reused?.wrap_algorithm || (deviceKey.publicKey.algorithm.name === 'X25519' ? 'X25519-AES-GCM' : 'RSA-OAEP-256');
  const base = { workspace_id: workspaceId, profile_id: profileId, kid, device_id: deviceKey.deviceId, wrap_type: 'device' as const };
  await capture.assertCurrent();
  return { local: { ...base, account_id: capture.scope.accountId, wrapped_seed: localCopy?.wrapped_seed || encoded,
    wrap_algorithm: localCopy?.wrap_algorithm || algorithm, created_at: localCopy?.created_at || new Date().toISOString() },
    remote: { ...base, wrapped_seed: remoteCopy?.wrapped_seed || encoded, wrap_algorithm: remoteCopy?.wrap_algorithm || algorithm } };
}

/** An approving sender cannot decrypt another device's prior envelope: retain it. */
async function prepareTargetDeviceSeed(workspaceId: string, profileId: string, kid: string, deviceId: string,
  publicKey: CryptoKey, seed: ArrayBuffer) {
  const copies = await keyClient.getWrappedSeeds(workspaceId, { device_id: deviceId, wrap_type: 'device' });
  const existing = copies.find(copy => copy.profile_id === profileId && copy.kid === kid);
  return { workspace_id: workspaceId, profile_id: profileId, kid, device_id: deviceId,
    wrapped_seed: existing?.wrapped_seed || btoa(String.fromCharCode(...new Uint8Array(await wrapSeed(seed, publicKey)))),
    wrap_algorithm: existing?.wrap_algorithm || (publicKey.algorithm.name === 'X25519' ? 'X25519-AES-GCM' : 'RSA-OAEP-256') };
}
